import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { passwordPolicyError, PASSWORD_HELP_TEXT } from "../lib/passwordPolicy";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { KeyRound, Mail } from "lucide-react";

// Segurança Pré-Produção, Pacote 5 (2FA por e-mail para alteração de
// senha) — substitui o antigo modal "Alterar senha" de etapa única
// (senha atual + nova senha -> pronto) por um fluxo de DUAS etapas: uma
// sessão aberta sozinha nunca é suficiente para trocar a senha em
// definitivo (item 1 do pedido). Componente ÚNICO, usado tanto pelo
// Layout operacional (tenant) quanto pelo AdminLayout (Admin Master) —
// nunca duas implementações do mesmo modal.
//
// ETAPA 1 (POST /auth/change-password/request-code): senha atual + nova
// senha — o backend confirma a senha atual, valida a política da nova
// senha e envia um código de 6 dígitos para o e-mail OFICIAL do próprio
// usuário (nunca escolhido aqui — o backend resolve sozinho); a senha
// ainda NÃO muda.
// ETAPA 2 (POST /auth/change-password/confirm): reenvia senha atual +
// nova senha + código — o backend revalida tudo do zero antes de mudar a
// senha (a nova senha nunca é persistida em lugar nenhum entre as duas
// etapas; este componente a mantém só em memória/estado do React
// enquanto o usuário preenche o código). Sucesso encerra a sessão atual
// (o backend já removeu os cookies e invalidou o token) — este
// componente completa o mesmo efeito localmente (logout() + redireciona
// para /login), nunca deixando o usuário "meio autenticado".
export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=solicitar, 2=confirmar
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [code, setCode] = useState("");
  const [maskedEmail, setMaskedEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const reset = () => {
    setStep(1); setCur(""); setNw(""); setCode(""); setMaskedEmail(""); setCooldown(0);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const requestCode = async () => {
    const pwErr = passwordPolicyError(nw);
    if (pwErr) { toast.error(pwErr); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password/request-code", { current_password: cur, new_password: nw });
      setMaskedEmail(data.masked_email || "");
      setCooldown(data.resend_cooldown_sec || 60);
      setStep(2);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password/request-code", { current_password: cur, new_password: nw });
      setMaskedEmail(data.masked_email || "");
      setCooldown(data.resend_cooldown_sec || 60);
      toast.success("Novo código enviado");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const confirm = async () => {
    if (!/^\d{6}$/.test(code)) { toast.error("Informe os 6 dígitos do código."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password/confirm", { current_password: cur, new_password: nw, code });
      toast.success("Senha alterada. Faça login novamente com a nova senha.");
      onOpenChange(false);
      reset();
      // A confirmação já encerrou a sessão no backend (cookies removidos,
      // token_version incrementado) — completa o mesmo efeito no cliente
      // e manda o usuário para o login, em vez de deixá-lo com um token
      // local que não funciona mais.
      await logout();
      navigate("/login", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const cancel = () => { onOpenChange(false); reset(); };

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) cancel(); }}>
      <DialogContent>
        <DialogHeader><DialogTitle>Alterar senha</DialogTitle></DialogHeader>
        {step === 1 && (
          <>
            <div className="space-y-3">
              <div><Label>Senha atual</Label><Input type="password" value={cur} onChange={e => setCur(e.target.value)} autoComplete="current-password" data-testid="cur-pw" /></div>
              <div>
                <Label>Nova senha</Label>
                <Input type="password" value={nw} onChange={e => setNw(e.target.value)} autoComplete="new-password" data-testid="new-pw" />
                <p className="text-xs text-muted-foreground mt-1">{PASSWORD_HELP_TEXT}</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cancel}>Cancelar</Button>
              <Button onClick={requestCode} disabled={busy || !cur || !nw} data-testid="request-change-pw-code">
                <Mail size={16} /> {busy ? "Enviando..." : "Enviar código de confirmação"}
              </Button>
            </DialogFooter>
          </>
        )}
        {step === 2 && (
          <>
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground" data-testid="change-pw-masked-email">
                Digite o código enviado por e-mail para concluir a alteração. Enviamos um código de confirmação para{" "}
                <span className="font-medium text-foreground">{maskedEmail}</span>.
              </p>
              <div>
                <Label>Código de 6 dígitos</Label>
                <Input
                  inputMode="numeric" pattern="\d{6}" maxLength={6}
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  autoFocus className="text-center text-2xl tracking-[0.5em]" placeholder="000000"
                  data-testid="change-pw-code"
                />
              </div>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={resend} disabled={cooldown > 0 || busy} className="text-primary hover:underline disabled:text-muted-foreground disabled:no-underline" data-testid="resend-change-pw-code">
                  {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
                </button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={cancel}>Cancelar</Button>
              <Button onClick={confirm} disabled={busy || code.length !== 6} data-testid="submit-change-pw">
                <KeyRound size={16} /> {busy ? "Confirmando..." : "Confirmar e alterar senha"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
